import {
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  ExternalLinkIcon,
  HamburgerIcon,
  SearchIcon,
  TriangleDownIcon,
} from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  ResponsiveValue,
  Spacer,
  Spinner,
  Text,
  Textarea,
  useColorMode,
  useDisclosure,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, {
  ChangeEventHandler,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as yup from 'yup';
import getConfig from '../common/config';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { useAppContext } from '../contexts/app';
import useAnnounce from '../hooks/useAnnounce';
import useApiClient from '../hooks/useApiClient';
import useAppRouter from '../hooks/useAppRouter';
import useIdApiClient from '../hooks/useIdApiClient';
/* 2026.04.03 初回ログイン時のスライド投稿誘導を非表示
import useImportantNotice from '../hooks/useImportantNotice';
*/
// import useOndemandProfileUpdate from '../hooks/useOndemandProfileUpdate';
import { isAccountIntegrated } from '../common/libs/accountIntegrationStatus';
import { isAntaaAccount } from '../common/libs/accountType';
import {
  isDoctorRecruiterAccount,
  isPharmaceuticalAccount,
} from '../common/libs/user';
import { useClickOutside } from '../hooks/useClickOutside';
import { useLogout } from '../hooks/useLogout';
import { usePopularKeywords } from '../hooks/usePopularKeywords';
import { usePrefixedWithKeywords } from '../hooks/usePrefixedWithKeywords';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { useSearchHistoryKeywords } from '../hooks/useSearchHistoryKeywords';
import BadgeModal from './BadgeModal';
import { IoBookmark, IoNotifications } from './Icons';

function MenuItemLink({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <MenuItem onClick={onClick}>
      <NextLink href={href} passHref>
        <Link color="linkText" w="100%">
          {children}
        </Link>
      </NextLink>
    </MenuItem>
  );
}

function MenuItemBadge({ userId }: { userId: number }) {
  const badgeDisclosure = useDisclosure();
  return (
    <MenuItem>
      <Link
        color="linkText"
        onClick={() => {
          sendAnalyticsEvent('badge', 'show', 'from_top_bar');
          badgeDisclosure.onOpen();
        }}
      >
        取得したバッジ
      </Link>
      <BadgeModal userId={userId} disclosure={badgeDisclosure} />
    </MenuItem>
  );
}

function MenuItemQaLink() {
  const {
    publicRuntimeConfig: { qaWebBaseUrl },
  } = getConfig();

  return (
    <MenuItem>
      <Link
        color="linkText"
        href={qaWebBaseUrl}
        isExternal
        onClick={() => sendAnalyticsEvent('link_to', 'qa', 'from_top_bar')}
      >
        Antaa QA <ExternalLinkIcon ml={1} mb={1} />
      </Link>
    </MenuItem>
  );
}

function MenuItemSpecial() {
  return (
    <MenuItem>
      <Link
        color="linkText"
        href={'/special'}
        rel="noopener"
        target="_self"
        isExternal
        onClick={() => sendAnalyticsEvent('link_to', 'special', 'from_top_bar')}
      >
        特集TOP
      </Link>
    </MenuItem>
  );
}

function MenuItemWeeklyRankingLink() {
  return (
    <MenuItem>
      <Link
        color="linkText"
        href={'/weekly-ranking-articles'}
        rel="noopener"
        target="_self"
        isExternal
        onClick={() =>
          sendAnalyticsEvent('link_to', 'weekly_ranking', 'from_top_bar')
        }
      >
        週間スライドランキング
      </Link>
    </MenuItem>
  );
}

function MenuItemHelpLink() {
  const noteAntaaHelpUrl = 'https://note.com/antaa/n/n2c814e46032c';
  return (
    <MenuItem>
      <Link
        color="linkText"
        href={noteAntaaHelpUrl}
        isExternal
        onClick={() => sendAnalyticsEvent('link_to', 'note', 'help_page')}
      >
        ヘルプ <ExternalLinkIcon ml={1} mb={1} />
      </Link>
    </MenuItem>
  );
}

function MenuItemFaqLink() {
  const noteAntaaFaqUrl = 'https://note.com/antaa/n/ne7676bee6c70';
  return (
    <MenuItem>
      <Link
        color="linkText"
        href={noteAntaaFaqUrl}
        isExternal
        onClick={() =>
          sendAnalyticsEvent('link_to', 'note', 'faq_page_from_navbar')
        }
      >
        FAQ <ExternalLinkIcon ml={1} mb={1} />
      </Link>
    </MenuItem>
  );
}

function ArticleUploadButtonLink() {
  const { isAuthenticated, hasDemoAuthority, user } = useAppContext();
  if (hasDemoAuthority || isPharmaceuticalAccount(user)) {
    return <></>;
  }
  return (
    <Box ml={1}>
      <NextLink href="/article/upload" passHref>
        <Button
          as="a"
          px={6}
          size="md"
          fontSize="sm"
          color="white"
          bg="brand.500"
          onClick={() =>
            sendAnalyticsEvent(
              'slide',
              'click',
              isAuthenticated ? 'upload_button' : 'upload_button_without_login',
            )
          }
        >
          スライド投稿
        </Button>
      </NextLink>
    </Box>
  );
}

function LoggedInMenuList({ user }: { user: Express.User }) {
  const { hasManageAuthority, accountIntegrationStatus } = useAppContext();
  const { proceedIdPage } = useIdApiClient();
  const { logout } = useLogout();
  const unlessAntaaAccount = !isAntaaAccount(user.accountType);
  const onClickExit = () => {
    sendAnalyticsEvent('profile', 'click', 'exit_button');
    proceedIdPage('/users/exit?fromService=slide', user.discloseId);
  };

  return (
    <MenuList zIndex="dropdown">
      <MenuItemLink
        href={`/profile/${user.discloseId || user.id}`}
        onClick={() => sendAnalyticsEvent('profile', 'click', 'mypage_button')}
      >
        プロフィール
      </MenuItemLink>
      {!isPharmaceuticalAccount(user) && (
        <>
          <MenuItemLink
            href="/article/upload"
            onClick={() =>
              sendAnalyticsEvent('slide', 'click', 'upload_button')
            }
          >
            スライド投稿
          </MenuItemLink>
          <MenuItemLink
            href="/mypage"
            onClick={() =>
              sendAnalyticsEvent('profile', 'click', 'setting_button')
            }
          >
            投稿したスライド
          </MenuItemLink>
          {!isDoctorRecruiterAccount(user) && (
            <MenuItemLink
              href="/mypage/like"
              onClick={() =>
                sendAnalyticsEvent('slide', 'click', 'stock_button')
              }
            >
              保存したスライド
            </MenuItemLink>
          )}
        </>
      )}
      <MenuItemLink
        href="/mypage/view-history"
        onClick={() =>
          sendAnalyticsEvent('slide', 'click', 'show_history_button')
        }
      >
        閲覧したスライド
      </MenuItemLink>
      {!isPharmaceuticalAccount(user) && <MenuItemBadge userId={user.id} />}
      {unlessAntaaAccount && (
        <MenuItemLink
          href="/mypage/password"
          onClick={() =>
            sendAnalyticsEvent('profile', 'click', 'change_password_button')
          }
        >
          パスワード変更
        </MenuItemLink>
      )}
      <MenuItemHelpLink />
      <MenuItemFaqLink />
      <MenuDivider />
      {!isPharmaceuticalAccount(user) && <MenuItemSpecial />}
      <MenuItemWeeklyRankingLink />
      <MenuDivider />
      <MenuItemQaLink />
      <MenuDivider />
      <MenuItem onClick={() => logout()}>ログアウト</MenuItem>
      {isAccountIntegrated(accountIntegrationStatus) &&
        !isPharmaceuticalAccount(user) &&
        !isDoctorRecruiterAccount(user) && (
          <MenuItemLink href="" onClick={onClickExit}>
            退会
          </MenuItemLink>
        )}
      {hasManageAuthority && (
        <>
          <MenuDivider />
          <MenuGroup title="管理者向け">
            <MenuItemLink href="/manager/users">ユーザー管理</MenuItemLink>
            <MenuItemLink href="/manager/articles">スライド管理</MenuItemLink>
            <MenuItemLink href="/manager/articles/upload">
              スライド アップロード
            </MenuItemLink>
            <MenuItemLink href="/manager/special-articles">
              特集ページ管理
              <Badge
                variant="solid"
                colorScheme="brand"
                rounded="full"
                verticalAlign="baseline"
                px={2}
                ml={2}
              >
                NEW
              </Badge>
            </MenuItemLink>
          </MenuGroup>
        </>
      )}
    </MenuList>
  );
}

function ProfileImage({ user }: { user: Express.User }) {
  return (
    <Image
      src="/images/avatar_default_gray.png"
      // fallbackSrc="/images/avatar_default_gray.png"
      w="8"
      h="8"
      minW="8"
      minH="8"
      alt={`${user.lastName || ''}${user.firstName || ''}`}
      objectFit="cover"
      borderRadius="full"
    />
  );
}

function LoggedInMenu({ user }: { user: Express.User }) {
  const onOpen = () => {
    sendAnalyticsEvent('hamburger', 'click', 'loggedin');
  };
  return (
    <>
      {/* for Mobile */}
      <Box display={[null, null, null, 'none']}>
        <Menu placement="bottom-end" computePositionOnMount onOpen={onOpen}>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<HamburgerIcon boxSize={6} />}
            variant="none"
          />
          <LoggedInMenuList user={user} />
        </Menu>
      </Box>

      {/* for PC */}
      <Box display={['none', null, null, 'block']}>
        <Menu placement="bottom-end" computePositionOnMount onOpen={onOpen}>
          <MenuButton
            as={Button}
            aria-label="Options"
            rightIcon={<ChevronDownIcon ms={-1} />}
            fontSize="sm"
            variant="none"
            px={0}
          >
            <ProfileImage user={user} />
          </MenuButton>
          <LoggedInMenuList user={user} />
        </Menu>
      </Box>
    </>
  );
}

function LoggedOutMenu({ loginUrl }: { loginUrl: string }) {
  const onOpen = () => {
    sendAnalyticsEvent('hamburger', 'click', 'loggedout');
  };
  return (
    <Menu placement="bottom-end" computePositionOnMount onOpen={onOpen}>
      <MenuButton
        as={IconButton}
        aria-label="Options"
        icon={<HamburgerIcon boxSize={6} />}
        variant="none"
      />
      <MenuList zIndex="dropdown">
        <MenuItemLink href={loginUrl}>ログイン</MenuItemLink>
        <MenuDivider />
        <MenuItemSpecial />
        <MenuItemWeeklyRankingLink />
        <MenuDivider />
        <MenuItemQaLink />
      </MenuList>
    </Menu>
  );
}

function SearchButton({
  keyword,
  setIsOpenPopover,
  isSubmitting,
}: {
  keyword: string;
  setIsOpenPopover: (isOpen: boolean) => void;
  isSubmitting: boolean;
}) {
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)');

  // デスクトップビュー
  if (isLargerThanMd) {
    return (
      <InputGroup
        mr={3}
        w={[null, null, '32%', '32%']}
        onClick={() => {
          setIsOpenPopover(true);
        }}
      >
        <InputLeftElement>
          {!isSubmitting && <SearchIcon color="textPrimary" />}
          {isSubmitting && (
            <Spinner color="textPrimary" speed="0.65s" size="sm" />
          )}
        </InputLeftElement>
        <Input
          borderColor="searchBar"
          bg="backgroundLight"
          textOverflow="ellipsis"
          borderRadius={8}
          color="textPrimary"
          isReadOnly
          onClick={() => {
            sendAnalyticsEvent('search', 'click', 'search_input_from_navbar');
          }}
          fontSize="sm"
          value={keyword}
          placeholder="キーワードや投稿者名、自然言語で検索"
        />
      </InputGroup>
    );
  }
  // モバイルビュー
  else {
    return (
      <IconButton
        aria-label="スライドを検索"
        icon={
          <>
            <VStack spacing={0}>
              <SearchIcon color="blackAlpha.600" boxSize={5} />
              <Text fontSize="xx-small" color="blackAlpha.600">
                検索
              </Text>
            </VStack>
          </>
        }
        variant="link"
        onClick={() => {
          sendAnalyticsEvent('search', 'click', 'search_input_from_navbar');
          setIsOpenPopover(true);
        }}
        mt={2} // 縦方向の表示位置の微調整
      />
    );
  }
}

type PrefixedKeywordListProps = {
  keywords: string[];
  setIsOpenPopover: (isOpen: boolean) => void;
};
const PrefixedKeywordList: React.FC<PrefixedKeywordListProps> = ({
  keywords,
  setIsOpenPopover,
}) => {
  return (
    <>
      {keywords.map((keyword) => {
        return (
          <NextLink
            key={keyword}
            href={`/search?q=${encodeURIComponent(keyword)}`}
            passHref
          >
            <Link
              color="linkText"
              _hover={{ textDecoration: 'none' }}
              onClick={(e) => {
                sendAnalyticsEvent(
                  'slide',
                  'index',
                  'suggested_keyword_search',
                );
                setIsOpenPopover(false);
              }}
            >
              <HStack
                px={4}
                py={4}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text fontSize="0.875rem">{keyword}</Text>
                <ChevronRightIcon color="#999999" boxSize={6} />
              </HStack>
            </Link>
          </NextLink>
        );
      })}
    </>
  );
};

type PopularKeywordListProps = {
  keywords: string[];
  setIsOpenPopover: (isOpen: boolean) => void;
};
const PopularKeywordList: React.FC<PopularKeywordListProps> = ({
  keywords,
  setIsOpenPopover,
}) => {
  return (
    <>
      <Box px={4} pt={6}>
        <Text as="h4" fontSize="0.75rem" fontWeight={300} color="textSecondary">
          自然言語の文章例
        </Text>
      </Box>
      <NextLink
        href={`/search?q=${encodeURIComponent(
          '高血圧の基礎から実臨床に関するスライドはありますか',
        )}`}
        passHref
      >
        <Link
          color="linkText"
          _hover={{ textDecoration: 'none' }}
          onClick={(e) => {
            sendAnalyticsEvent('slide', 'index', 'popular_keyword_search');
            setIsOpenPopover(false);
          }}
        >
          <HStack
            px={4}
            py={4}
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="0.875rem" fontWeight={300} ml={2}>
              高血圧の基礎から実臨床に関するスライドはありますか
            </Text>
            <ChevronRightIcon color="#999999" boxSize={6} />
          </HStack>
        </Link>
      </NextLink>
      <hr />
      <Box px={4} pt={6}>
        <Text as="h4" fontSize="0.75rem" fontWeight={300} color="textSecondary">
          人気キーワード
        </Text>
      </Box>
      {keywords.map((keyword) => {
        return (
          <NextLink
            key={keyword}
            href={`/search?q=${encodeURIComponent(keyword)}`}
            passHref
          >
            <Link
              color="linkText"
              _hover={{ textDecoration: 'none' }}
              onClick={(e) => {
                sendAnalyticsEvent('slide', 'index', 'popular_keyword_search');
                setIsOpenPopover(false);
              }}
            >
              <HStack
                px={4}
                py={4}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text fontSize="0.875rem" ml={2}>
                  {keyword}
                </Text>
                <ChevronRightIcon color="#999999" boxSize={6} />
              </HStack>
            </Link>
          </NextLink>
        );
      })}
    </>
  );
};

type SearchHistoryListProps = {
  keywords: string[];
  setIsOpenPopover: (isOpen: boolean) => void;
  deleteSearchHistoryKeywords: (keyword: string) => Promise<void>;
};
const SearchHistoryList: React.FC<SearchHistoryListProps> = ({
  keywords,
  setIsOpenPopover,
  deleteSearchHistoryKeywords,
}) => {
  return (
    <>
      <Box px={4} py={4}>
        <Text as="h4" fontSize="0.75rem" fontWeight={300} color="textSecondary">
          検索履歴
        </Text>
      </Box>
      {keywords.map((keyword) => {
        return (
          <NextLink
            key={keyword}
            href={`/search?q=${encodeURIComponent(keyword)}`}
            passHref
          >
            <Link
              color="linkText"
              _hover={{ textDecoration: 'none' }}
              onClick={(e) => {
                sendAnalyticsEvent(
                  'slide',
                  'index',
                  'search_history_keyword_search',
                );
                setIsOpenPopover(false);
              }}
            >
              <HStack
                px={4}
                py={4}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text fontSize="0.875rem" flex={1} noOfLines={2} ml={2}>
                  {keyword}
                </Text>
                <CloseIcon
                  color="#999999"
                  boxSize={3}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    sendAnalyticsEvent(
                      'slide',
                      'click',
                      'search_history_keyword_delete',
                    );
                    deleteSearchHistoryKeywords(keyword);
                  }}
                />
              </HStack>
            </Link>
          </NextLink>
        );
      })}
    </>
  );
};

type SlideSearchPopoverProps = {
  keyword: string;
  setKeyword: (keyword: string) => void;
  setIsOpenPopover: (isOpen: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
};
const SlideSearchPopover: React.FC<SlideSearchPopoverProps> = ({
  keyword,
  setKeyword,
  setIsOpenPopover,
  setIsSubmitting,
}) => {
  const router = useAppRouter();
  const { isAuthenticated } = useAppContext();
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resizeObserver = useResizeObserver<HTMLDivElement>();
  const popoverContainerRef = useClickOutside<HTMLDivElement>(() => {
    setIsOpenPopover(false);
  });

  // 検索予測キーワード
  const { prefixedWithKeywords, fetchPrefixedWithKeywords } =
    usePrefixedWithKeywords();

  // 人気キーワード
  const { popularKeywords, fetchPopularKeywords } = usePopularKeywords();

  // 検索履歴
  const {
    searchHistoryKeywords,
    fetchSearchHistoryKeywords,
    deleteSearchHistoryKeywords,
  } = useSearchHistoryKeywords();

  // 人気キーワードの取得
  useEffect(() => {
    fetchPopularKeywords();
  }, [fetchPopularKeywords]);

  // 検索履歴の取得
  useEffect(() => {
    if (isAuthenticated) {
      fetchSearchHistoryKeywords();
    }
  }, [fetchSearchHistoryKeywords, isAuthenticated]);

  // 入力文字数に応じてTextareaのリサイズ
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && textarea.value) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  });

  const handleChangeKeyword: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setKeyword(e.target.value);
    const trimmed = e.target.value.trim();
    if (trimmed.length > 0) {
      fetchPrefixedWithKeywords(trimmed);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Enterでsubmitさせる
    if (e.keyCode === 13) {
      e.preventDefault();
      setIsSubmitting(true);
      setIsOpenPopover(false);
      sendAnalyticsEvent('slide', 'index', 'keyword');
      router.push(`/search?q=${encodeURIComponent(keyword)}`).then(() => {
        setIsSubmitting(false);
      });
    }
  };

  // デスクトップビュー
  if (isLargerThanMd) {
    return (
      <Box
        ref={popoverContainerRef}
        mr={3}
        w={[null, null, '32%', '32%']}
        alignSelf="flex-start"
        shadow="xl"
        rounded="4px"
        px="4px"
        pt="4px"
        bg="white"
        maxH="calc(100vh - 32px)"
      >
        <Box ref={resizeObserver.ref}>
          <InputGroup>
            <InputLeftElement>
              <SearchIcon color="textSecondary" fontSize="1.125rem" />
            </InputLeftElement>
            <Textarea
              ref={textareaRef}
              autoFocus
              maxH="calc(100vh - 400px)"
              resize="none"
              px="40px"
              rows={1}
              value={keyword}
              onChange={handleChangeKeyword}
              onKeyDown={handleKeyDown}
            />
            <InputRightElement>
              <CloseIcon
                color="textSecondary"
                fontSize="0.875rem"
                cursor="pointer"
                onClick={() => {
                  setKeyword('');
                }}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
        <Box
          maxH={`calc(100vh - ${resizeObserver.dimensions.height}px - 64px)`}
          overflowY="scroll"
        >
          {!!keyword.trim() && (
            <>
              <Box px={4} py={6}>
                <HStack spacing={2}>
                  <Text noOfLines={1} fontSize="0.93rem" fontWeight={600}>
                    {keyword}
                  </Text>
                  <Text ml={1} flexShrink={0} fontSize="0.93rem">
                    を検索
                  </Text>
                </HStack>
              </Box>
              {prefixedWithKeywords.length > 0 && <hr />}
              <PrefixedKeywordList
                keywords={prefixedWithKeywords}
                setIsOpenPopover={setIsOpenPopover}
              />
            </>
          )}
          {!keyword.trim() && searchHistoryKeywords.length === 0 && (
            <PopularKeywordList
              keywords={popularKeywords}
              setIsOpenPopover={setIsOpenPopover}
            />
          )}
          {!keyword.trim() && searchHistoryKeywords.length > 0 && (
            <SearchHistoryList
              keywords={searchHistoryKeywords}
              setIsOpenPopover={setIsOpenPopover}
              deleteSearchHistoryKeywords={deleteSearchHistoryKeywords}
            />
          )}
        </Box>
      </Box>
    );
  }
  // モバイルビュー
  else {
    return (
      <Box
        pos="fixed"
        top={0}
        left={0}
        zIndex={5000}
        bg="white"
        w="100%"
        h="100vh"
        overflowY="scroll"
      >
        <Flex px={4} py={2} justifyContent="flex-end">
          <Text
            fontSize="0.875rem"
            fontWeight={600}
            color="textLink"
            cursor="pointer"
            onClick={() => {
              setIsOpenPopover(false);
            }}
          >
            キャンセル
          </Text>
        </Flex>
        <Box px={4}>
          <InputGroup>
            <InputLeftElement>
              <SearchIcon color="textSecondary" fontSize="1.125rem" />
            </InputLeftElement>
            <Textarea
              ref={textareaRef}
              autoFocus
              maxH="180px"
              resize="none"
              px="40px"
              rows={1}
              value={keyword}
              onChange={handleChangeKeyword}
              onKeyDown={handleKeyDown}
            />
            <InputRightElement>
              <CloseIcon
                color="textSecondary"
                fontSize="0.875rem"
                cursor="pointer"
                onClick={() => {
                  setKeyword('');
                }}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
        {!!keyword.trim() && (
          <>
            <Box px={4} py={6}>
              <HStack spacing={2}>
                <Text noOfLines={1} fontSize="0.93rem" fontWeight={600}>
                  {keyword}
                </Text>
                <Text ml={1} flexShrink={0} fontSize="0.93rem">
                  を検索
                </Text>
              </HStack>
            </Box>
            {prefixedWithKeywords.length > 0 && <hr />}
            <PrefixedKeywordList
              keywords={prefixedWithKeywords}
              setIsOpenPopover={setIsOpenPopover}
            />
          </>
        )}
        {!keyword.trim() && searchHistoryKeywords.length === 0 && (
          <PopularKeywordList
            keywords={popularKeywords}
            setIsOpenPopover={setIsOpenPopover}
          />
        )}
        {!keyword.trim() && searchHistoryKeywords.length > 0 && (
          <SearchHistoryList
            keywords={searchHistoryKeywords}
            setIsOpenPopover={setIsOpenPopover}
            deleteSearchHistoryKeywords={deleteSearchHistoryKeywords}
          />
        )}
      </Box>
    );
  }
};

type SearchInputProps = {
  keyword: string;
  setIsOpenPopover: (isOpen: boolean) => void;
};
const SearchInput: React.FC<SearchInputProps> = ({
  keyword,
  setIsOpenPopover,
}) => {
  return (
    <Box w="100%" bg="white" px={4}>
      <InputGroup py={1} bg="white" display={['block', null, 'none']}>
        <InputLeftElement>
          <SearchIcon mt={3} color="blackAlpha.900" />
        </InputLeftElement>
        <Input
          borderColor="backgroundLight"
          borderRadius="8px"
          bg="backgroundLight"
          css={{
            '&::-webkit-search-cancel-button': {
              display: 'none',
            },
          }}
          isReadOnly
          onClick={() => {
            sendAnalyticsEvent(
              'search',
              'click',
              'search_input_from_keyword_search_result',
            );
            setIsOpenPopover(true);
          }}
          value={keyword}
          noOfLines={1}
        />
      </InputGroup>
    </Box>
  );
};

function LikeArticlesButtonLink() {
  const { user } = useAppContext();
  if (isPharmaceuticalAccount(user) || isDoctorRecruiterAccount(user)) {
    return <></>;
  }
  return (
    <NextLink href="/mypage/like" passHref>
      <IconButton
        as="a"
        aria-label="保存リストを表示"
        icon={
          <>
            <VStack spacing={0} color="blackAlpha.600">
              <IoBookmark size={19} />
              <Text fontSize="xx-small">保存リスト</Text>
            </VStack>
          </>
        }
        variant="link"
        onClick={() => sendAnalyticsEvent('slide', 'click', 'stock_button')}
        mt={2} // 縦方向の表示位置を微調整
        mx={1}
      />
    </NextLink>
  );
}

function AnnounceButtonLink() {
  const { isAuthenticated, announceModalDisclosure } = useAppContext();
  const { fetchAnnounces, hasUnread } = useAnnounce();

  // モーダル開閉時の処理
  useEffect(() => {
    fetchAnnounces();
  }, [isAuthenticated, fetchAnnounces]);

  return (
    <IconButton
      as="a"
      href="#"
      aria-label="お知らせを表示"
      icon={
        <>
          <VStack spacing={0} color="blackAlpha.600">
            <IoNotifications size={20} />
            <Text fontSize="xx-small">お知らせ</Text>
          </VStack>
          {isAuthenticated && hasUnread && (
            <Box
              pos="absolute"
              right={2}
              top={0}
              w={2}
              h={2}
              borderRadius={'full'}
              bgColor={'red'}
              color="red"
            />
          )}
        </>
      }
      variant="link"
      onClick={(e) => {
        e.preventDefault();
        sendAnalyticsEvent('slide', 'click', 'announce_button');
        announceModalDisclosure.onOpen();
      }}
      mt={2} // 縦方向の表示位置を微調整
      mx={1}
    ></IconButton>
  );
}

export default function NavBar({
  pos = 'fixed',
}: {
  pos?: ResponsiveValue<'fixed'>;
}) {
  const router = useAppRouter();
  const {
    user,
    isAuthenticated,
    // importantNoticeModalDisclosure,
    // ondemandProfileUpdateModalDisclosure,
  } = useAppContext();
  const { colorMode } = useColorMode();
  /* 2026.04.03 初回ログイン時のスライド投稿誘導を非表示
  const { fetchImportantNotice, hasImportantNoticeUnread } =
    useImportantNotice();
  */
  const [keyword, setKeyword] = useState(
    router.query.q ? yup.string().required().validateSync(router.query.q) : '',
  );
  const [isOpenSearchPopover, setIsOpenSearchPopover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)');
  /* 2023.08.15 オンデマンドプロフィール更新誘導無効化
  const {
    fetchOndemandProfileUpdate,
    incrementSuggestCountOndemandProfileUpdate,
    showOndemandProfileUpdateModal,
    idOndemandProfileUpdateModal,
  } = useOndemandProfileUpdate();
  */
  const pathname = useRouter().pathname;
  const isTopPage = pathname === '/';
  const isLoginPage = pathname === '/login';
  const isSpecialPage = pathname.startsWith('/special');
  const isSlideSearchPage = pathname.startsWith('/search');
  const redirectPath =
    pathname === '/login' ||
    pathname === '/logout' ||
    router.asPath.startsWith('//')
      ? '/'
      : router.asPath;
  const loginUrl = `/login?redirectUrl=${encodeURIComponent(redirectPath)}`;

  const shouldShowSearchInput = isSlideSearchPage && !isLargerThanMd;

  let zIndex: ResponsiveValue<'sticky'> | undefined;
  if (pos === 'fixed') {
    zIndex = 'sticky';
  } else if (Array.isArray(pos)) {
    zIndex = pos.map((v) => (v === 'fixed' ? 'sticky' : null));
  }

  useEffect(() => {
    setKeyword(
      router.query.q
        ? yup.string().required().validateSync(router.query.q)
        : '',
    );
  }, [router.query.q]);

  /* 2026.04.03 初回ログイン時のスライド投稿誘導を非表示
  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        await fetchImportantNotice();
        if (hasImportantNoticeUnread) {
          sendAnalyticsEvent('important_notice', 'open', null);
          importantNoticeModalDisclosure.onOpen();
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchImportantNotice, hasImportantNoticeUnread, isAuthenticated]);
  */

  /* 2023.08.15 オンデマンドプロフィール更新誘導無効化
  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        await fetchOndemandProfileUpdate();

        if (showOndemandProfileUpdateModal) {
          sendAnalyticsEvent('ondemand_profile_update', 'open', null);
          ondemandProfileUpdateModalDisclosure.onOpen();

          if (idOndemandProfileUpdateModal) {
            await incrementSuggestCountOndemandProfileUpdate();
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchOndemandProfileUpdate,
    incrementSuggestCountOndemandProfileUpdate,
    showOndemandProfileUpdateModal,
    idOndemandProfileUpdateModal,
    isAuthenticated,
  ]);
  */
  const { apiClient } = useApiClient();

  const [isClicked, setIsClicked] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { departmentsPopoverDisclosure } = useAppContext();
  const toggleClick = () => {
    setIsClicked(!isClicked);
  };

  const LogoImage = () => (
    <NextImage
      src={
        colorMode === 'light'
          ? '/images/logo_slide.png'
          : '/images/logo_slide_white.png'
      }
      alt="Antaa Slide"
      width="164"
      height="33"
    />
  );

  return (
    <>
      <Box bg="navbar" pos={pos} zIndex={zIndex} width="100vw">
        <Flex
          height={['56px', null, null, '70px']}
          py={26}
          px={[17, null, null, 39]}
          alignItems="center"
        >
          <Link
            href="/"
            display="flex"
            translateY="2px"
            transform="auto"
            flexShrink={0}
            _hover={{
              textDecoration: 'note',
              opacity: 0.8,
            }}
          >
            <Box height={[23, 33, 58, 57]} width={[122, 170, 170, 164]}>
              <Text
                fontSize="10"
                pl={1}
                pt={1}
                display={['none', 'none', 'flex', 'flex']}
                color="black"
              >
                医師・医学生のためのスライド共有
              </Text>
              {isTopPage ? (
                <Heading as="h1" lineHeight="none">
                  <LogoImage />
                </Heading>
              ) : (
                <LogoImage />
              )}
            </Box>
          </Link>
          {!isLoginPage && (
            <Box
              display={{ base: 'none', md: 'block', lg: 'block', xl: 'block' }}
              ml={[2, null, 2, 8]}
              pos="relative"
            >
              <Flex justify="center" align-items="flex-start" align="center">
                <Box
                  mr={[2, null, 2, 4]}
                  borderBottomWidth={isOpen ? 2 : 0}
                  borderColor="black"
                >
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleClick;
                    }}
                    onMouseEnter={() => {
                      departmentsPopoverDisclosure.onOpen();
                    }}
                    color="black"
                    display="flex"
                    alignItems="center"
                  >
                    <Text as="span" fontSize="sm" fontWeight="normal" ms={1}>
                      診療科
                    </Text>
                    <Icon
                      as={TriangleDownIcon}
                      w={3}
                      h={3}
                      ms={1}
                      verticalAlign="bottom"
                    />
                  </Link>
                </Box>
                {!isPharmaceuticalAccount(user) && (
                  <Box borderBottomWidth={0} borderColor="black">
                    <Link href="/special" color="black" pb={2}>
                      <Text
                        as="span"
                        fontSize="sm"
                        fontWeight="normal"
                        ms={1}
                        color={isSpecialPage ? '#008e14' : '#222'}
                      >
                        特集
                      </Text>
                    </Link>
                  </Box>
                )}
              </Flex>
            </Box>
          )}
          <Spacer />
          {!isOpenSearchPopover ? (
            <SearchButton
              keyword={keyword}
              setIsOpenPopover={setIsOpenSearchPopover}
              isSubmitting={isSubmitting}
            />
          ) : (
            <SlideSearchPopover
              keyword={keyword}
              setKeyword={setKeyword}
              setIsOpenPopover={setIsOpenSearchPopover}
              setIsSubmitting={setIsSubmitting}
            />
          )}
          {!isPharmaceuticalAccount(user) && <AnnounceButtonLink />}
          {!isAuthenticated && (
            <NextLink href={loginUrl} passHref>
              <Button
                as="a"
                px={[3, 6]}
                ml={2}
                size="md"
                fontSize={['xs', 'sm']}
                lineHeight={[1.2, 'normal']}
                // 狭い画面（sm未満）では文言を2行に折り返してボタン幅を抑える
                flexDirection={['column', 'row']}
                color="white"
                bg="brand.500"
                // 2026-10-09：ログインボタン文言変更施策
                onClick={() =>
                  sendAnalyticsEvent(
                    'link_to',
                    'click',
                    'login_or_signup_20261009',
                  )
                }
              >
                <Box as="span">登録 /</Box>
                <Box as="span" ml={[0, 1]}>
                  ログイン
                </Box>
              </Button>
            </NextLink>
          )}
          {isAuthenticated ? (
            <>
              <LikeArticlesButtonLink />
              <Box pos="relative" zIndex="dropdown" ml={2}>
                <LoggedInMenu user={user!} />
              </Box>
              <Box display={['none', null, null, 'flex']}>
                <ArticleUploadButtonLink />
              </Box>
            </>
          ) : (
            <Box
              pos="relative"
              display={[null, null, null, 'none']}
              translateY="2px"
              transform="auto"
              zIndex="dropdown"
            >
              <LoggedOutMenu loginUrl={loginUrl} />
            </Box>
          )}
        </Flex>
      </Box>
      {shouldShowSearchInput && (
        <Box
          pos="sticky"
          top="0"
          zIndex="docked"
          pb={1}
          bg="white"
          borderBottomWidth={1}
          shadow="navbar"
        >
          <SearchInput
            keyword={keyword}
            setIsOpenPopover={setIsOpenSearchPopover}
          />
        </Box>
      )}
    </>
  );
}
